/**
 * FeedbackDialog — "earn a free credit" feedback form.
 *
 * Submissions go to the founder's inbox along with a one-time 1-credit code;
 * he reviews and forwards the code, so the success copy promises the code by
 * email within 24 hours (not instantly).
 */
import { useState } from 'react';

import { BackendAPI } from '../../lib/api';
import type { ApiError } from '../../types';
import { Modal } from './ui/modal';
import { Button } from './ui/button';

const MIN_CHARS = 50;

/** Props for {@link FeedbackDialog}. */
export interface FeedbackDialogProps {
  onClose: () => void;
}

/** Feedback modal with a min-length textarea and 24h-code success state. */
export default function FeedbackDialog({ onClose }: FeedbackDialogProps) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const remaining = Math.max(0, MIN_CHARS - body.trim().length);

  const submit = async () => {
    if (body.trim().length < MIN_CHARS) {
      setError(`Please write at least ${MIN_CHARS} characters so we can act on it.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await BackendAPI.submitFeedback({ body: body.trim() });
      setSubmitted(true);
    } catch (err) {
      const apiErr = err as ApiError;
      setError(
        apiErr.status === 429
          ? 'You’ve reached the feedback reward limit for this account.'
          : apiErr.message || 'Could not submit feedback. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <Modal
        label="Feedback submitted"
        title="Thank you — feedback submitted"
        onClose={onClose}
        testId="feedback-success"
        footer={
          <Button variant="primary" size="sm" onClick={onClose}>
            Done
          </Button>
        }
      >
        <p className="text-[12.5px] text-[var(--tx-mid)]">
          We read every submission. Check your inbox — you should receive a one-time
          credit code by email <strong>within 24 hours</strong>.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      label="Give feedback"
      title="Earn a free credit — give feedback"
      subtitle="Tell us what worked and what didn’t. Useful feedback earns a 1-credit code by email."
      onClose={onClose}
      testId="feedback-dialog"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void submit()}
            disabled={busy || body.trim().length < MIN_CHARS}
          >
            {busy ? 'Submitting…' : 'Submit feedback'}
          </Button>
        </>
      }
    >
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={6}
        placeholder="What did you import? What looked right, what looked wrong? What would make this worth paying for?"
        aria-label="Feedback"
        className="w-full rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-3 py-2 text-[13px] text-[var(--tx-hi)] outline-none focus:border-[var(--accent-line)]"
        disabled={busy}
      />
      <p className="mt-1 text-[11px] text-[var(--tx-lo)]">
        {remaining > 0 ? `${remaining} more characters needed` : 'Ready to submit'}
      </p>
      {error && (
        <p className="mt-2 text-[12px]" style={{ color: 'var(--bad)' }} role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}
